import {combineLatest} from 'rxjs';
import {filter, map, scan, skipUntil, withLatestFrom} from 'rxjs/operators';
import {strArrDiff} from '@/helper/utils';
import {toWsMsg$, wsMsg$} from '@/ws';
import {wsLogin$} from '@/user/auth-ws';
import {mainUserId$, contractSubUserIds$, contractAirdropPosSubUserIds$} from '@/raw/users';
import {aopUserIsMatched, findAopUid} from '@/raw/utils';
import type {ws} from '@/types';
type AOPMsg = ws.AOPMsg;
type AOPMsgV2 = ws.AOPMsgV2;

// 过滤出未订阅的账户，防止重复订阅
// 新增子账号暂时会订阅失败，原因是：做auth验证时，后端会缓存最新用户数据，此时并没有新增的子账号，auth会认证失败
const subUserIds$ = combineLatest([contractSubUserIds$, contractAirdropPosSubUserIds$]).pipe(
  skipUntil(wsLogin$),
  map(([userIds, airdropPosSubUserIds]) => [...userIds, ...airdropPosSubUserIds]),
  scan(strArrDiff(), {origin: '', add: []})
);

subUserIds$
  .pipe(
    skipUntil(wsLogin$),
    filter((userIds) => userIds.add.length > 0)
  )
  .subscribe((userIds) => sub(userIds.add));

wsLogin$
  .pipe(
    withLatestFrom(subUserIds$),
    filter(([, userIds]) => userIds.origin.length > 0)
  )
  .subscribe(([, userIds]) => {
    const userIds2 = userIds.origin.split(',').map((o) => Number(o));
    sub(userIds2);
  });

function sub(userIds: number[]) {
  if (userIds.length > 0) {
    const params = genAopParams(userIds);
    toWsMsg$.next({method: 'aop_v1.subscribe', params});
    toWsMsg$.next({method: 'aop_p1.subscribe', params});
  }
}

export function genAopParams(userId: number | number[], subscribe0Pos = false) {
  /**
   * aop 订阅参数
   * {method: 'aop_v1.subscribe', params: params}
   * {method: 'aop_v1.unsubscribe', params: params}
   * params: [order_limit, publish_zero_pos, skip_zero_field, user_id/user_ids, skip_create_zero_position]
   * order_limit      委托限制: close 和 filled 的大小
   * publish_zero_pos 头寸为0的要不要推送 头寸为0且有过尺寸或者调整过仓位，比如修改过杠杆
   * skip_zero_field  为0的字段是否要推送
   * skip_create_zero_position 为0后端不再创建0仓位数据，节省资源，对前端来说与第二个参数表现一样
   */
  return [0, subscribe0Pos, true, userId, true];
}

export const aopMsgAll$ = wsMsg$.pipe(
  filter((o: AOPMsg) => !!o.accounts && !!o.orders && !!o.positions),
  withLatestFrom(mainUserId$),
  filter(([o, mainUserId]) => !aopUserIsMatched(o, mainUserId)),
  map(([o]) => {
    o.accounts = ensureTradeLevel(o.accounts);
    o.positions = addTradeLevel(o.positions, o.accounts);
    return {
      userId: findAopUid(o),
      data: {
        ...o,
      },
    };
  })
);

export const aopMsgV2All$ = wsMsg$.pipe(
  filter((o: AOPMsgV2) => !!o.accounts_p1 && !!o.orders_p1 && !!o.positions_p1),
  withLatestFrom(mainUserId$),
  filter(([o, userId]) => !aopUserIsMatched(o, userId)),
  map(([o]) => {
    const {accounts_p1, orders_p1, positions_p1, events_p1, ...rest} = o;
    const accounts = ensureTradeLevel(accounts_p1);
    return {
      userId: findAopUid(o),
      data: {
        ...rest,
        accounts,
        orders: orders_p1,
        positions: addTradeLevel(positions_p1, accounts),
        events: events_p1,
      },
    };
  })
);

function ensureTradeLevel<T extends ws.ContractAccountRaw | ws.ContractAccountRawV2>(accounts: T[]): T[] {
  accounts.forEach((account) => {
    account.tradeLevel = account.tradeLevel || 0;
  });
  return accounts;
}

function addTradeLevel(
  positions: ws.ContractPositionRaw[],
  accounts: (ws.ContractAccountRaw | ws.ContractAccountRawV2)[]
) {
  return positions.map((position) => {
    const account = accounts.find((o) => o.accountID === position.accountID);
    if (account) {
      position.tradeLevel = account.tradeLevel;
    }
    return position;
  });
}
